import { computed, ref, watchEffect } from 'vue'
import type { NavItems, NavLink } from '@/interfaces/nav'
import i18n from '@/plugins/i18n'
import { brandColours } from '@/plugins/vuetify/brandColours'
import { useProgrammeHelper } from '@/composable/queries/helpers/useProgrammeHelper'
import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { UserProfilePermissionLevel } from '@/enums/UserProfilePermissionLevel'
import { checkPermissions } from '@/composable/useCheckPermissions'
import { useRoute } from 'vue-router'

export function useOrganisationNavigation() {
  const appStore = useAppStore()
  const authStore = useAuthStore()
  const route = useRoute()
  const { organisationProgrammes } = useProgrammeHelper()

  const { organisationIsBusinessPartnerClient, selected_organisation_id } = storeToRefs(authStore)

  const organisationProgrammeItems = ref<NavLink[]>([])

  // VueRouter does not expose an onclick event for us to manually tweak the filters like we do in tab switching.
  // The sub nav links use a programme_id filter, there isn't one for overview so we need to refresh the whole page or else vuerouter blocks the route change.
  const isOnStudentsOverview = computed(() => route.name === 'students.overview')

  watchEffect(() => {
    if (selected_organisation_id && organisationProgrammes.value) {
      organisationProgrammeItems.value = organisationProgrammes.value
        .filter(programme => programme.has_questionnaire)
        .sort((a, b) => a.display_order - b.display_order)
        .map(programme => ({
          title: programme.name,
          to: {
            name: 'students.overview',
            hash: `#filters=${JSON.stringify({ programme_id: programme.id })}`,
          },
          color: brandColours[programme.theme as keyof typeof brandColours],
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        }))
    } else {
      organisationProgrammeItems.value = []
    }
  })

  const navItems = computed(() => [
    {
      title: i18n.global.t('nav_titles.students'),
      icon: 'mdi-account-multiple',
      permission: UserProfilePermissionLevel.STUDENT_VIEWER,
      subNav: [
        {
          title: i18n.global.t('page_titles.students_overview'),
          ...(isOnStudentsOverview.value
            ? { href: '/students/overview' }
            : { to: { name: 'students.overview' } }
          ),
          icon: 'mdi-table-account',
          subNavAdditional: organisationProgrammeItems.value,
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        },
        {
          title: i18n.global.t('page_titles.add_students'),
          to: { name: 'students.add-options' },
          icon: 'mdi-account-multiple-plus',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
        },
        {
          title: i18n.global.t('page_titles.saf_wizard'),
          to: { name: 'students.saf' },
          icon: 'mdi-account-school',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
        },
      ],
    },
    {
      title: i18n.global.t('nav_titles.licences'),
      icon: 'mdi-account-key',
      permission: UserProfilePermissionLevel.STUDENT_MANAGER,
      subNav: [
        {
          title: i18n.global.t('page_titles.licence_overview'),
          to: { name: 'licences.overview' },
          icon: 'mdi-file-document-outline',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
        },
        {
          title: i18n.global.t('page_titles.licence_usage_history'),
          to: { name: 'licences.usage-history' },
          icon: 'mdi-clipboard-text-clock',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
        },
        {
          title: i18n.global.t('page_titles.partner_agreement'),
          to: { name: 'licences.agreement' },
          icon: 'mdi-file-sign',
          permission: UserProfilePermissionLevel.ADMIN,
        },
      ],
    },
    {
      title: i18n.global.t('page_titles.interview_request'),
      to: { name: 'interviews.request.list' },
      icon: 'mdi-calendar-month',
      permission: UserProfilePermissionLevel.STUDENT_MANAGER,
    },
    {
      title: i18n.global.t('page_titles.activities'),
      to: { name: 'activities.overview' },
      icon: 'mdi-checkbox-multiple-marked',
      permission: UserProfilePermissionLevel.STUDENT_MANAGER,
    },
    {
      title: i18n.global.t('nav_titles.resources'),
      icon: 'mdi-account-multiple',
      permission: UserProfilePermissionLevel.STUDENT_VIEWER,
      subNav: [
        {
          title: i18n.global.t('page_titles.resource_library'),
          to: { name: 'resource-library' },
          icon: 'mdi-library',
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        },
        {
          title: i18n.global.t('page_titles.requested_downloads'),
          to: { name: 'organisation.requested-downloads' },
          icon: 'mdi-file-multiple',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
        },
        {
          title: i18n.global.t('nav_titles.dcd'),
          href: `${appStore.config.DCD_BASE_URL}?return-app=myhq-v3`,
          icon: 'mdi-text-box-multiple',
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        },
      ],
    },
    {
      title: i18n.global.t('nav_titles.admin'),
      icon: 'mdi-book-cog-outline',
      permission: UserProfilePermissionLevel.STUDENT_VIEWER,
      subNav: [
        {
          title: i18n.global.t('page_titles.organisation_settings'),
          to: { name: 'organisation.settings' },
          icon: 'mdi-cog',
          permission: UserProfilePermissionLevel.ADMIN,
        },
        {
          title: i18n.global.t('page_titles.student_password_settings'),
          to: { name: 'organisation.student-password-settings' },
          icon: 'mdi-lock',
          permission: UserProfilePermissionLevel.STUDENT_MANAGER,
          excludePermissions: [UserProfilePermissionLevel.ADMIN],
        },
        {
          title: i18n.global.t('page_titles.student_access_settings'),
          to: { name: 'organisation.student-access-settings.list' },
          icon: 'mdi-eye-outline',
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        },
        {
          title: i18n.global.t('page_titles.staff'),
          to: { name: 'organisation.staff.list' },
          icon: 'mdi-account',
          permission: UserProfilePermissionLevel.ADMIN,
        },
        {
          title: i18n.global.t('page_titles.year_groups'),
          to: { name: 'organisation.year-groups.list' },
          icon: 'mdi-account-group',
          permission: UserProfilePermissionLevel.ADMIN,
        },
        {
          title: i18n.global.t('page_titles.qualifications_and_subjects'),
          to: { name: 'organisation.subjects-and-qualifications' },
          icon: 'mdi-book-open-page-variant-outline',
          permission: UserProfilePermissionLevel.STUDENT_VIEWER,
        },
      ],
    },
  ])

  const isPageAuthorised = (page: NavLink) => {
    if (page.excludePermissions?.includes(authStore.userProfilePermissionLevel)) {
      return false
    }

    if (page.permission === undefined) {
      return true
    }

    return checkPermissions(page.permission)
  }

  const filterNavItems = (items: NavItems, isBusinessPartnerClient: boolean): NavItems => {
    items = items.filter(item => isPageAuthorised(item))
      .map(item => {
        if (Array.isArray(item.subNav)) {
          return {
            ...item,
            subNav: item.subNav.filter(subpage => isPageAuthorised(subpage)),
          } as NavLink
        }

        return item as NavLink
      })

    if (!isBusinessPartnerClient) return items

    let updatedNavItems = items.map(item => {
      if (Array.isArray(item.subNav)) {
        return {
          ...item,
          subNav: item.subNav.filter(subItem => subItem.title !== i18n.global.t('page_titles.partner_agreement')),
        } as NavLink
      }

      return item as NavLink
    })

    updatedNavItems = updatedNavItems.filter(item => item.title !== i18n.global.t('page_titles.interview_request'))

    return updatedNavItems
  }

  const filteredNavItems = computed(() =>

    filterNavItems(
      navItems.value,
      organisationIsBusinessPartnerClient.value ?? false,
    ),
  )

  return {
    navItems: filteredNavItems,
  }
}
